import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import classNames from "classnames"
import { Badge, Divider, IconButton, Paper } from "@mui/material"
import { FilterList, ResetTvOutlined } from "@mui/icons-material"
import { isEmpty, isNil, isUndefined, omitBy, some } from "lodash"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import CompareDateSelector from "connected-components/compare/components/date-selector"
import ListItemDropDown from "components/list-item-dropdown"
import OPTooltip from "components/material/tooltip"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useIsDashboardOptimizeExperimentsFeatureEnabled, useAccountAlias, useAccountId, useUserId, useUserName, useUserEmail } from "resources/account"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { OK, ERROR, DATA_STATUSES } from "helpers/data-statuses"
import { CUSTOM_PERIOD, SAME_DAY } from "helpers/dates"
import { computeIntradayAsOfDisplay } from "helpers/intraday"
import { createReduceDownloadData } from "./helpers/download-aggregator"
import { downloadRegistry } from "./context/download-registry"
import useIsConstituentsDownloadAllowed from "hooks/use-is-constituents-download-allowed"
import { getDate } from "modules/intraday/selectors/dates"
import { getOrCreateIntradaySocket } from "socket/intraday-connection"
import { useAutoRunOptimization } from "connected-components/optimize-experiments/hooks/use-auto-run-optimization"
import { getOptimizationResultExperimentName } from "connected-components/optimize-experiments/helpers/get-optimization-position-set"
import { useOptimizationPositionSet } from "connected-components/optimize-experiments/hooks/use-optimization-position-set"
import { MODEL_METADATA_SHAPE, INSTRUMENT_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import OptimizeExperimentsPanel from "connected-components/optimize-experiments/components/optimize-experiments-panel"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "./context/dashboard-id"
import { DEFAULT_CORE_STATE } from "./store/core-slice"
import { DEFAULT_OPTIMIZATION_STATE } from "./store/optimization-slice"
import { DEFAULT_TRADE_STATE } from "./store/trade-slice"
import { useCoreActions } from "./hooks/use-core-actions"
import { useTradeActions } from "./hooks/use-trade-actions"
import DashboardModulesRenderer from "./components/dashboard-modules-renderer"
import DashboardDownload from "./components/download"
import FilterSelector from "./components/filter-selector"
import TradeManager from "./components/trader-manager"
import { getModeSelectorOptions } from "./helpers/mode-selector"
import { getDownloadDataFileName } from "./helpers/download"
import { getIndexPositionSets } from "./helpers/positions"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS, useDashboardLoadedTracking, useModeChangeTracking, useModelChangeTracking } from "./analytics"
import { useAdjustedDashboardDates } from "./hooks/dates"
import { useIntradayWarningNotification } from "./hooks/use-intraday-warning-notification"
import { useIntradayRefresh } from "./hooks/use-intraday-refresh"
import useMixPanel from "hooks/use-mixpanel"
import { useIsMixpanelSessionReplayEnabled } from "resources/account"
import IntradayDataInfo from "./components/intraday-data-info"
import LatestPriceDisplay from "./components/latest-price-display"
import DashboardTopBar from "./components/top-bar"
import { RULES } from "connected-components/compare/position-sets"
import { usePinArtifactHandler } from "./hooks/use-pin-artifact-handler"
import { useRemoveArtifactHandler } from "./hooks/use-remove-artifact-handler"

import "./style.scss"

const PERIOD_SELECTOR_LABELS = {
  from: "Start",
  to: "End (As of)",
}

export default function Dashboard(props) {
  const dashboardId = useDashboardId()

  const core = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const {
    instrument: original,
    filter,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    selectedMode,
    period,
    startDate,
    endDate,
    useLatestDate,
    isIntraday,
    isEndDateIntraday,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isEndVsStartDateComparison,
    isPreferencesLoaded,
    base,
  } = core
  const {
    optimizeConfiguration,
    isOptimizePanelExpanded,
    hasUserAppliedOptimization,
    optimizationSourceKind,
    optimizationExperimentSource,
  } = useAppSelector((s) => s.dashboard.optimization[dashboardId] ?? DEFAULT_OPTIMIZATION_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)

  const tradeState = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)
  const { trades } = tradeState

  const {
    setInstrument,
    setComparisonInstrument,
    setSelectedMode,
    setPeriod,
    setBoundaryDates,
    setUseLatestDate,
    setPerformanceStartDate,
    setPerformanceEndDate,
    setIsEndDateIntraday,
    setOptimizeConfiguration,
    setModelId,
    setIsOptimizePanelExpanded,
    setIntradayBatchMetadata,
    setIntradayPricingAsOf,
  } = useCoreActions()

  const { resetTrades } = useTradeActions()

  const {
    modelMetadata,
    instrumentsWithMetadata,
    contentSets,
    hasMultipleInstruments,
    instrumentMetadata,
    comparisonInstrumentMetadata,
    baseInstrumentMetadata,
    lastIntradayBatch,
    lastIntradayBatchStatus,
  } = props

  const analytics = useAnalytics()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const userName = useUserName()
  const userEmail = useUserEmail()
  const isMixpanelSessionReplayEnabled = useIsMixpanelSessionReplayEnabled()

  const mixpanelEventProperties = useMemo(() => omitBy({
    source: "dashboard",
    account_id: accountId,
    account_alias: accountAlias,
    instrument_type: original?.type,
    instrument_id: original?.id,
  }, isUndefined), [accountId, accountAlias, original?.type, original?.id])

  useMixPanel({
    distinctId: isMixpanelSessionReplayEnabled ? userId : null,
    eventProperties: mixpanelEventProperties,
    name: userName,
    email: userEmail,
  })
  useDashboardLoadedTracking()
  useModeChangeTracking(selectedMode)
  useModelChangeTracking(modelId)

  const optimizationPositionSet = useOptimizationPositionSet(
    original,
    optimizationSourceKind,
    optimizationExperimentSource,
  )

  const optimizationResultExperimentName = useMemo(
    () => getOptimizationResultExperimentName(optimizationSourceKind, optimizationExperimentSource),
    [optimizationSourceKind, optimizationExperimentSource],
  )

  const [filterButtonAnchor, setFilterButtonAnchor] = useState(null)
  const [filterSelectorStatus, setFilterSelectorStatus] = useState(false)

  const dockviewLayoutRef = useRef(null)

  const dispatch = useDispatch()
  const [layoutActions, setLayoutActions] = useState(null)
  const reduceDownloadData = useMemo(() =>
    createReduceDownloadData(() => layoutActions?.getPanelOrder?.() ?? []),
  [layoutActions],
  )

  useEffect(() => {
    return () => downloadRegistry.clear()
  }, [])

  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && lastIntradayBatchStatus === OK

  useIntradayWarningNotification()

  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  useAutoRunOptimization(optimizationPositionSet, modelId, endDate, base, setOptimizeConfiguration, optimizeConfiguration, setComparisonInstrument, isOptimizeExperimentsEnabled, isOptimizePanelExpanded, hasUserAppliedOptimization, optimizationResultExperimentName)

  const { currentAsOf, intradayRelativeDay } = useMemo(
    () => computeIntradayAsOfDisplay(intradayBatchMetadata),
    [intradayBatchMetadata],
  )

  const isIntradayInstrument = !!instrumentsWithMetadata?.original?.intradayEnabled

  const latestPriceTimestamp = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateIntraday || !intradayPricingAsOf) return null
    return moment(intradayPricingAsOf).format("LTS")
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateIntraday, intradayPricingAsOf])

  useEffect(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateIntraday) return
    const socket = getOrCreateIntradaySocket(accountId)

    function handleTimestampUpdate({ timestamp }) {
      setIntradayPricingAsOf(timestamp)
    }

    socket.on("timestampUpdated", handleTimestampUpdate)
    return () => {
      socket.off("timestampUpdated", handleTimestampUpdate)
    }
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateIntraday, accountId, setIntradayPricingAsOf])

  const intradayDate = getDate()

  useEffect(() => {
    setIsEndDateIntraday(endDate === intradayDate)
  }, [endDate, intradayDate, setIsEndDateIntraday])

  const modeSelectorOptions = useMemo(() =>
    getModeSelectorOptions(rebalanced, comparison, isEndVsStartDateComparison),
  [rebalanced, comparison, isEndVsStartDateComparison])

  const handleFiltersToggle = useCallback((event) => {
    if (filterButtonAnchor) {
      analytics.track(DASHBOARD_EVENTS.FILTERS_CLOSED)
      setFilterButtonAnchor(null)
    } else {
      analytics.track(DASHBOARD_EVENTS.FILTERS_OPENED)
      setFilterButtonAnchor(event.currentTarget)
    }
  }, [filterButtonAnchor, setFilterButtonAnchor, analytics])

  const { adjustedStartDate, earliestDate, latestDate, unmodeledDates, datesAdjusted, isLoading: isLoadingDates } = useAdjustedDashboardDates({
    modelMetadata,
    instrumentsWithMetadata,
    period,
    startDate,
    endDate,
    useLatestDate,
    setUseLatestDate,
    setPeriod,
    setBoundaryDates,
    setPerformanceStartDate,
    setPerformanceEndDate,
    trackPeriodShortcutDateChanged: (oldStartDate, newStartDate, newEndDate, period) => analytics.track(DASHBOARD_EVENTS.DATE_SHORTCUT_CHANGED, {
      oldStartDate,
      newStartDate,
      newEndDate,
      period,
    }),
    instrument: original,
    isIntraday,
    isIntradayAnalyticsEnabled,
  })

  const filtersCount = useMemo(() => {
    if (!filter) {
      return 0
    }
    return Object.keys(filter).length
  }, [filter])

  const isFiltersBadgeVisible = !filterButtonAnchor && filtersCount > 0

  useEffect(() => {
    if (instrumentMetadata?.unavailable) {
      setInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The main instrument could not be found and was reset."))
    } else if (comparisonInstrumentMetadata?.unavailable) {
      setComparisonInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The comparison instrument could not be found and was reset."))
    }
  }, [instrumentMetadata, setInstrument, setComparisonInstrument, comparisonInstrumentMetadata, dispatch])

  useEffect(() => {
    if (!isIntraday || !lastIntradayBatch || trades?.length > 0) {
      return
    }

    if (isIntraday && intradayDataAvailable && !isEndDateIntraday) {
      return
    }

    if (lastIntradayBatch.asOf !== intradayBatchMetadata?.asOf) {
      setIntradayBatchMetadata({
        date: lastIntradayBatch.date,
        asOf: lastIntradayBatch.asOf,
        id: lastIntradayBatch.id,
      })
    }
  }, [isIntraday, lastIntradayBatch, setIntradayBatchMetadata, intradayBatchMetadata, trades, intradayDataAvailable, isEndDateIntraday])

  useEffect(() => {
    if (isEndVsStartDateComparison && original && comparison?.id !== original.id) {
      setComparisonInstrument(original)
    } else if (comparison?.type === RULES && comparison.rulesConfiguration.source.positionSet.id !== original.id) {
      setComparisonInstrument({
        type: RULES,
        rulesConfiguration: {
          source: { positionSet: { id: original.id, type: original.type } },
          rules: [...comparison.rulesConfiguration.rules],
        },
      })
    }
  }, [isEndVsStartDateComparison, setComparisonInstrument, original, comparison])

  useEffect(() => {
    if (!modeSelectorOptions || !selectedMode) {
      return
    }

    const isSelectedModeAvailable = some(modeSelectorOptions, option => option.id === selectedMode && !option.disabled)
    if (!isSelectedModeAvailable) {
      const newMode = modeSelectorOptions.find(option => !option.disabled)
      if (newMode) {
        setSelectedMode(newMode.id)
      }
    }
  }, [modeSelectorOptions, selectedMode, setSelectedMode])

  const setPeriodWithChange = useCallback((change) => {
    if (typeof change === "string") {
      setPeriod(change)
    } else {
      const newStartDate = change.adjusted?.startDate || change.startDate
      const newEndDate = change.adjusted?.endDate || change.endDate
      setPeriod({
        period: CUSTOM_PERIOD,
        startDate: newStartDate,
        endDate: newEndDate,
      })

      setUseLatestDate(newEndDate === intradayDate)
      analytics.track(DASHBOARD_EVENTS.CUSTOM_DATES_CHANGED, {
        oldStartDate: startDate,
        oldEndDate: endDate,
        newStartDate: newStartDate,
        newEndDate: newEndDate,
      })
    }
  }, [setPeriod, startDate, endDate, analytics, setUseLatestDate, intradayDate])

  const customizeOptions = useCallback((options) => [
    { value: SAME_DAY, label: "Same Day" },
    ...options,
  ], [])

  const handleDateChangeWithTradesCheck = useCallback((handleDateAction) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Date Change & Clear All Trades",
        contentText: "Changing the date will reset your trades. Would you still like to proceed with the date change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            handleDateAction()
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      handleDateAction()
    }
  }, [trades, dispatch, resetTrades, analytics])

  const handleModelChange = useCallback((newModelId) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Model Change & Clear All Trades",
        contentText: "Changing the model will reset your trades. Would you still like to proceed with the model change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            setModelId(newModelId)
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      setModelId(newModelId)
    }
  }, [trades, dispatch, setModelId, resetTrades, analytics])

  const onDownloadData = useCallback(() => ({ data: reduceDownloadData(), fileName: getDownloadDataFileName(instrumentMetadata, isIntraday, latestPriceTimestamp) }), [reduceDownloadData, instrumentMetadata, isIntraday, latestPriceTimestamp])

  const [isDownloadAllowedStatus, isDownloadAllowed] = useIsConstituentsDownloadAllowed({ modelId, positionSets: getIndexPositionSets([instrumentMetadata, comparisonInstrumentMetadata]) })

  const hasOptimizationComparison = useMemo(() => {
    return !isNil(comparison) && isOptimizeExperimentsEnabled && comparison.isOptimization
  }, [comparison, isOptimizeExperimentsEnabled])

  const { showRefreshButton, showWarning, handleIntradayRefresh, refreshButtonTooltip } = useIntradayRefresh(
    isIntraday,
    intradayDataAvailable,
    useLatestDate,
    lastIntradayBatch,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    setPeriod,
    period,
    adjustedStartDate,
    trades,
    resetTrades,
    onDownloadData,
    isPreferencesLoaded,
    isEndDateIntraday,
    lastIntradayBatchStatus,
    setUseLatestDate,
    setComparisonInstrument,
    setIsOptimizePanelExpanded,
    hasOptimizationComparison,
  )

  const handleSetPeriod = useCallback(change => {
    handleDateChangeWithTradesCheck(
      () => setPeriodWithChange(change),
    )
  }, [handleDateChangeWithTradesCheck, setPeriodWithChange])

  const handleResetEndDate = useCallback(() => {
    const resetDate = isIntraday ? intradayDate : latestDate

    setUseLatestDate(true)
    handleDateChangeWithTradesCheck(
      () => {
        setPeriod({
          period,
          endDate: resetDate,
          startDate: period === CUSTOM_PERIOD ? startDate : adjustedStartDate,
        })
        analytics.track(DASHBOARD_EVENTS.END_DATE_RESET_TO_LATEST, { oldEndDate: endDate, newEndDate: resetDate })
      },
    )
  }, [isIntraday, intradayDate, latestDate, setUseLatestDate, handleDateChangeWithTradesCheck, setPeriod, period, startDate, adjustedStartDate, analytics, endDate])

  const tooltipContent = <div>
    <div>Selected period: {period}</div>
    <br />
    <div><b>As of</b> date applies to point in time metrics; <b>Start</b> date used for performance, comparisons, & trends.</div>
  </div>

  return (
    <div className="modules_dashboard-root">
      <DashboardTopBar
        instrumentMetadata={instrumentMetadata}
        comparisonInstrumentMetadata={comparisonInstrumentMetadata}
        baseInstrumentMetadata={baseInstrumentMetadata}
        adjustedStartDate={adjustedStartDate}
        onModelChange={handleModelChange}
      />
      <Paper square className="secondary-selectors">
        <div className="left-selectors">
          <CompareDateSelector {...{
            period: period,
            earliestDate: earliestDate,
            latestDate: latestDate,
            startDate: adjustedStartDate,
            endDate: endDate,
            onChange: handleSetPeriod,
            datesAdjusted,
            unmodeledDates,
            handleResetOption: handleResetEndDate,
            customizeOptions,
            isIntradayEnabled: isIntraday,
            isIntradayBatchAvailable: intradayDataAvailable,
            isEndDateToday: isEndDateIntraday,
            intradayAsOf: currentAsOf,
            intradayRelativeDay,
            intradayBatchStatus: lastIntradayBatchStatus,
            skipAutoAdjustment: true,
            datesWithLabels: true,
            labels: PERIOD_SELECTOR_LABELS,
            tooltipContent,
          }}
          />
          {isIntradayInstrument && (
            <IntradayDataInfo
              isIntradayEnabled={isIntraday}
              isEndDateToday={isEndDateIntraday}
              currentAsOf={currentAsOf}
              intradayRelativeDay={intradayRelativeDay}
              showWarning={showWarning}
              showRefreshButton={showRefreshButton}
              onRefresh={handleIntradayRefresh}
              refreshButtonTooltip={refreshButtonTooltip}
              isLoadingDates={isLoadingDates}
              useLatestDate={useLatestDate}
            />
          )}
          {isIntradayAnalyticsEnabled && !isIntradayInstrument && latestPriceTimestamp && (
            <LatestPriceDisplay timestamp={latestPriceTimestamp} />
          )}
          {!isEmpty(modeSelectorOptions) && <>
            <Divider orientation="vertical" flexItem />
            <ListItemDropDown
              disableSubHeader
              classes={{
                toggle: "mode-selector",
              }}
              label="Showing"
              value={selectedMode}
              setValue={setSelectedMode}
              options={modeSelectorOptions}
              alignMenu="left"
              disablePortal
            />
          </>}
          <Divider orientation="vertical" flexItem />
          <TradeManager instrumentsWithMetadata={instrumentsWithMetadata} />
          <OPTooltip title="Reset Layout">
            <IconButton
              sx={{ marginLeft: -1 }}
              onClick={() => {
                const actions = layoutActions ?? dockviewLayoutRef.current
                const api = actions?.getApi?.()
                if (api) {
                  actions.resetLayout(api)
                }
              }}
              size="large"
            >
              <ResetTvOutlined />
            </IconButton>
          </OPTooltip>
        </div>
        <div className="right-selectors">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Divider orientation="vertical" flexItem />
            <DashboardDownload id="dashboard-download-button" instrumentMetadata={instrumentMetadata} isIntradayEnabled={isIntraday} latestPriceTimestamp={latestPriceTimestamp} disabled={isDownloadAllowedStatus !== OK || !isDownloadAllowed} reduceDownloadData={reduceDownloadData} />
            <OPTooltip title={filterSelectorStatus === ERROR ? "Error loading filters. Applying filters is disabled." : "Filter positions"}>
              <span>
                <IconButton
                  onClick={handleFiltersToggle}
                  size="large"
                  color={filterButtonAnchor ? "primary" : undefined}
                  disabled={filterSelectorStatus !== OK}
                  data-test-id="filters-toggle"
                >
                  <Badge overlap="rectangular" color="primary" invisible={!isFiltersBadgeVisible} badgeContent={filtersCount}>
                    <FilterList />
                  </Badge>
                </IconButton>
              </span>
            </OPTooltip>
          </div>
        </div>
      </Paper>
      <FilterSelector setFilterSelectorStatus={setFilterSelectorStatus} hasMultipleInstruments={hasMultipleInstruments} anchor={filterButtonAnchor} toggleFilters={handleFiltersToggle} />
      <div className={classNames("dashboard-content", { "optimizer-open": isOptimizeExperimentsEnabled && isOptimizePanelExpanded })}>
        {isOptimizeExperimentsEnabled && (
          <OptimizeExperimentsPanel
            isOptimizedExperimentsEnabled={isOptimizeExperimentsEnabled}
            modelMetadata={modelMetadata}
          />
        )}
        <ModulesEventListeners />
        <div className="dashboard-main-content">
          <DashboardModulesRenderer
            instrumentsWithMetadata={instrumentsWithMetadata}
            hasMultipleInstruments={hasMultipleInstruments}
            contentSets={contentSets}
            modelMetadata={modelMetadata}
            dockviewLayoutRef={dockviewLayoutRef}
            onLayoutReady={setLayoutActions}
          />
        </div>
      </div>
    </div>
  )
}

function ModulesEventListeners() {
  usePinArtifactHandler()
  useRemoveArtifactHandler()
  return null
}

Dashboard.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  instrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  comparisonInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  baseInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  instrumentsWithMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  lastIntradayBatch: PropTypes.object.isRequired,
  lastIntradayBatchStatus: PropTypes.oneOf(DATA_STATUSES).isRequired,
}
